import { sanitizeHtml } from '@vuejs/utils/stringUtils'
import type { ObjectDirective } from 'vue'

interface TooltipEvents {
	add: () => void
	remove: () => void
	update: (message: string | null) => void
}

export interface TooltipOptions {
	bottom?: boolean
	left?: boolean
	right?: boolean
	text: string
}

const tooltips = new WeakMap<HTMLElement, TooltipEvents>()

const tooltipDirective: ObjectDirective<HTMLElement, TooltipOptions | string | null> = {
	mounted(el, binding) {
		let tooltipText: string | null | undefined
		let bottom: boolean | undefined
		let left: boolean | undefined
		let right: boolean | undefined

		if (typeof binding.value === 'string') {
			bottom = binding.modifiers?.bottom
			left = binding.modifiers?.left
			right = binding.modifiers?.right
			tooltipText = binding.value
		} else if (typeof binding.value === 'object') {
			bottom = binding.value?.bottom
			left = binding.value?.left
			right = binding.value?.right
			tooltipText = binding.value?.text
		}

		function add() {
			if (!tooltipText) {
				remove()

				return
			}
			const existingTooltip = document.querySelector('.ca-tooltip')
			const tooltip = (existingTooltip ?? document.createElement('span')) as HTMLSpanElement

			tooltip.innerHTML = sanitizeHtml(tooltipText)

			el.style.cursor = 'pointer'
			el.style.position = 'relative'

			// Use body-append when the element itself clips overflow, or any ancestor does
			// (e.g. overflow: hidden for ellipsis, overflow: auto/scroll for scroll containers)
			const clipsOverflow = (styles: CSSStyleDeclaration) =>
				[
					'auto',
					'hidden',
					'scroll',
				].includes(styles.overflow) ||
				[
					'auto',
					'hidden',
					'scroll',
				].includes(styles.overflowY)

			const hasClippingAncestor = (() => {
				let parent = el.parentElement

				while (parent && parent !== document.body) {
					if (clipsOverflow(window.getComputedStyle(parent))) return true
					parent = parent.parentElement
				}

				return false
			})()

			const needsBodyAppend = clipsOverflow(window.getComputedStyle(el)) || hasClippingAncestor

			if (!needsBodyAppend) {
				el.style.overflow = 'visible'
			}

			tooltip.setAttribute('aria-hidden', 'true')
			tooltip.setAttribute('aria-describedby', 'tooltip')
			tooltip.setAttribute('data-test', 'ca-tooltip')
			tooltip.setAttribute('role', 'tooltip')
			tooltip.className = 'ca-tooltip'

			if (left) {
				tooltip.className += ' ca-tooltip--left'
			}

			if (right) {
				tooltip.className += ' ca-tooltip--right'
			}

			if (bottom) {
				tooltip.className += ' ca-tooltip--bottom'
			}

			// If element or an ancestor clips overflow, append tooltip to body and position it manually
			if (needsBodyAppend) {
				const rect = el.getBoundingClientRect()

				tooltip.style.position = 'fixed'

				/* eslint-disable no-magic-numbers */
				// Center horizontally above the element
				if (left) {
					tooltip.style.left = `${rect.left - 8}px`
					tooltip.style.top = `${rect.top + rect.height / 2}px`
					tooltip.style.transform = 'translate(-100%, -50%)'
				} else if (right) {
					tooltip.style.left = `${rect.right + 8}px`
					tooltip.style.top = `${rect.top + rect.height / 2}px`
					tooltip.style.transform = 'translate(0, -50%)'
				} else if (bottom) {
					tooltip.style.left = `${rect.left + rect.width / 2}px`
					tooltip.style.top = `${rect.bottom + 8}px`
					tooltip.style.transform = 'translateX(-50%)'
				} else {
					// Default: top center
					tooltip.style.left = `${rect.left + rect.width / 2}px`
					tooltip.style.top = `${rect.top - 8}px`
					tooltip.style.transform = 'translate(-50%, -100%)'
				}
				/* eslint-enable no-magic-numbers */

				document.body.appendChild(tooltip)
			} else {
				el.appendChild(tooltip)
			}
		}

		function remove() {
			// Check for tooltip as child of element
			const tooltip = el.querySelector('.ca-tooltip')

			if (tooltip) {
				el.removeChild(tooltip)
			}

			// Also check for tooltip in body (for overflow:hidden elements)
			const bodyTooltip = document.querySelector('.ca-tooltip')

			if (bodyTooltip && bodyTooltip.parentElement === document.body) {
				document.body.removeChild(bodyTooltip)
			}
		}

		function update(message: string | null) {
			tooltipText = message

			const tooltip = el.querySelector('.ca-tooltip')

			if (tooltip) {
				add()
			}
		}

		tooltips.set(el, {
			add,
			remove,
			update,
		})

		el.addEventListener('mouseenter', add)
		el.addEventListener('mouseleave', remove)
	},

	updated(el, binding) {
		if (typeof binding.value === 'string') {
			tooltips.get(el)?.update(binding.value)
		} else if (binding.value && typeof binding.value === 'object') {
			tooltips.get(el)?.update(binding.value.text)
		}
	},

	beforeUnmount(el) {
		const tooltipEvents = tooltips.get(el)

		if (tooltipEvents) {
			el.removeEventListener('mouseenter', tooltipEvents.add)
			el.removeEventListener('mouseleave', tooltipEvents.remove)
		}
	},
}

export default tooltipDirective
